/**
 * Button Component
 * Interactive button with variants
 */

import React, { forwardRef } from 'react';
import styled from '@emotion/styled';
import { transformStyleProps, splitProps, stylePropsKeys } from '../system/style-props';
import { useTheme } from '../theme';

/**
 * Base styled button
 */
const StyledButton = styled('button', {
  shouldForwardProp: (prop) => {
    if (stylePropsKeys.includes(prop)) return false;
    if (['as', 'sx', '__css', 'variant', 'size', 'colorScheme', 'isLoading', 
         'isDisabled', 'isActive', 'leftIcon', 'rightIcon', 'loadingText',
         'spinnerPlacement', 'isFullWidth'].includes(prop)) return false;
    return true;
  },
})(({ theme, ...props }) => {
  const styles = [];

  // Apply __css internal styles
  if (props.__css) {
    const cssResult = typeof props.__css === 'function'
      ? props.__css({ theme, colorMode: 'light', ...props })
      : props.__css;
    styles.push(transformStyleProps(cssResult, theme));
  }

  // Apply style props
  const [styleProps] = splitProps(props);
  styles.push(transformStyleProps(styleProps, theme));

  // Apply sx prop
  if (props.sx) {
    const sxResult = typeof props.sx === 'function'
      ? props.sx({ theme, ...props })
      : props.sx;
    styles.push(transformStyleProps(sxResult, theme));
  }

  return styles;
});

/**
 * Button Component
 * 
 * @example
 * <Button variant="brand" size="md">Click Me</Button>
 * <Button leftIcon={<Icon />} colorScheme="green">With Icon</Button>
 */
export const Button = forwardRef(({
  as,
  variant = 'solid',
  size = 'md',
  colorScheme = 'gray',
  isLoading = false,
  isDisabled = false,
  isActive = false,
  isFullWidth = false,
  leftIcon,
  rightIcon,
  loadingText,
  spinner,
  spinnerPlacement = 'start',
  children,
  type = 'button',
  ...props
}, ref) => {
  const theme = useTheme();
  const buttonStyles = theme?.components?.Button || {};
  
  // Get base styles
  const baseStyle = buttonStyles.baseStyle || {};
  
  // Get size styles
  const sizeStyle = buttonStyles.sizes?.[size] || {};
  
  // Get variant styles
  const variantStyle = typeof buttonStyles.variants?.[variant] === 'function'
    ? buttonStyles.variants[variant]({ colorScheme, colorMode: 'light' })
    : buttonStyles.variants?.[variant] || {};

  // Merge all styles
  const mergedStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    appearance: 'none',
    userSelect: 'none',
    position: 'relative',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    outline: 'none',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'gray.200',
    cursor: 'pointer',
    width: isFullWidth ? '100%' : 'auto',
    ...baseStyle,
    ...sizeStyle,
    ...variantStyle,
    ...(isDisabled || isLoading ? {
      opacity: 0.4,
      cursor: 'not-allowed',
      pointerEvents: 'none',
    } : {}),
    ...(isActive ? (variantStyle._active || {}) : {}),
  };

  // Remove pseudo-props from mergedStyles (they'll be handled by emotion)
  const cleanStyles = { ...mergedStyles };
  delete cleanStyles._hover;
  delete cleanStyles._active;
  delete cleanStyles._focus;
  delete cleanStyles._disabled;

  const buttonContent = (
    <>
      {isLoading && spinnerPlacement === 'start' && (
        spinner || <ButtonSpinner style={{ marginRight: '1rem' }} />
      )}
      {leftIcon && !isLoading && (
        <ButtonIcon style={{ marginRight: '0.5rem' }}>{leftIcon}</ButtonIcon>
      )}
      {isLoading ? loadingText || <span style={{ opacity: 0 }}>{children}</span> : children}
      {rightIcon && !isLoading && (
        <ButtonIcon style={{ marginLeft: '0.5rem' }}>{rightIcon}</ButtonIcon>
      )}
      {isLoading && spinnerPlacement === 'end' && (
        spinner || <ButtonSpinner ml={loadingText ? 2 : 0} />
      )}
    </>
  );

  return (
    <StyledButton
      ref={ref}
      as={as}
      type={type}
      disabled={isDisabled || isLoading}
      aria-disabled={isDisabled || isLoading}
      data-active={isActive || undefined}
      data-loading={isLoading || undefined}
      __css={cleanStyles}
      {...props}
    >
      {buttonContent}
    </StyledButton>
  );
});

Button.displayName = 'Button';

/**
 * ButtonIcon wrapper
 */
const ButtonIcon = ({ children, ...props }) => (
  <span style={{ display: 'inline-flex', alignSelf: 'center', flexShrink: 0, ...props }}>
    {children}
  </span>
);

/**
 * ButtonSpinner default loading indicator
 */
const ButtonSpinner = (props) => (
  <span
    style={{
      width: '1em',
      height: '1em',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'currentColor',
      borderBottomColor: 'transparent',
      borderRadius: '50%',
      animation: 'spin 0.75s linear infinite',
      display: 'inline-block',
      ...props,
    }}
  />
);

/**
 * ButtonGroup Component
 * Groups buttons together with consistent spacing
 */
export const ButtonGroup = forwardRef(({
  spacing = 2,
  isAttached = false,
  variant,
  size,
  colorScheme,
  isDisabled,
  children,
  ...props
}, ref) => {
  const childrenWithProps = React.Children.map(children, (child, index) => {
    if (!React.isValidElement(child)) return child;
    
    const isFirst = index === 0;
    const isLast = index === React.Children.count(children) - 1;
    
    return React.cloneElement(child, {
      variant: child.props.variant || variant,
      size: child.props.size || size,
      colorScheme: child.props.colorScheme || colorScheme,
      isDisabled: child.props.isDisabled !== undefined ? child.props.isDisabled : isDisabled,
      ...(isAttached ? {
        borderRadius: isFirst ? undefined : isLast ? undefined : 0,
        borderTopRightRadius: isLast ? undefined : 0,
        borderBottomRightRadius: isLast ? undefined : 0,
        borderTopLeftRadius: isFirst ? undefined : 0,
        borderBottomLeftRadius: isFirst ? undefined : 0,
      } : {}),
    });
  });

  return (
    <div
      ref={ref}
      role="group"
      style={{
        display: 'inline-flex',
        gap: isAttached ? 0 : `${spacing * 4}px`,
      }}
      {...props}
    >
      {childrenWithProps}
    </div>
  );
});

ButtonGroup.displayName = 'ButtonGroup';

/**
 * IconButton Component
 * Button with only an icon
 */
export const IconButton = forwardRef(({
  icon,
  'aria-label': ariaLabel,
  isRound = false,
  ...props
}, ref) => {
  return (
    <Button
      ref={ref}
      aria-label={ariaLabel}
      borderRadius={isRound ? 'full' : undefined}
      p={0}
      {...props}
    >
      {icon}
    </Button>
  );
});

IconButton.displayName = 'IconButton';

// Add keyframe animation for spinner
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);
}

export default Button;
